import { useState } from 'react';
import { useForm } from 'react-hook-form';
import style from './newApplicationForm.module.scss';
import { Button } from 'components/UI/button/button';
import DropDownSelect from 'components/UI/dropDownSelect/dropDownSelect';
import DatePicker from 'components/UI/DatePicker/datePicker';
import FileUpload, { DocumentAdditionParams, DocumentDeletionParams } from 'components/UI/FileUpload/fileUpload';
import { Input } from "components/UI/input/input";
import { DropdownOptions, useGetCountries } from 'actions/locationsActions';
import { fileToBase64, parseDateString, isEmptyDropDownValue, getFieldDocumentNames, parseFileExtensions } from 'utils/commonFunctions';
import { DocumentName, DocumentNames } from 'actions/documentActions';
import { validEmailRegex, validPhoneNumberRegex, validZipCodePostalCodeRegex } from 'utils/regexValidations';
import { AllowedExtensions } from 'utils/imageFunctions';
import { MultilineInput } from 'components/UI/multilineInput/multilineInput';
import { getUserEmailCheck } from 'actions/userActions';
import { isValid, parse, isFuture, subYears } from 'date-fns';
import translations from 'locales';

interface ApplicationFormProps {
  onSubmitForm: (values: ApplicationFormFields) => void
}

export interface ApplicationFormFields {
  textRequestDetails: string;
  fullName: string;
  lastName: string;
  firstName: string;
  birthday: string;
  nationality: string;
  passportNumber: string;
  title: string;
  streetName: string;
  unitNumber: string;
  town: string;
  province: string;
  postal: string;
  country: string;
  email: string;
  phone: string;
  department?: string;
  applicationType?: string;
  comments?: string;
  fileUploads?: base64Uploads;
  caseAnalyst?: string;
  assignedLawyer?: string;
  matterType?: string;
}

const departmentOptions = [
  { value: 'hr', label: 'Human Resources' },
  { value: 'it', label: 'Information Technology' },
  { value: 'finance', label: 'Finance' },
];

const applicationTypeOptions = [
  { value: 'full-time', label: 'Full-Time' },
  { value: 'part-time', label: 'Part-Time' },
  { value: 'intern', label: 'Internship' },
];

const titleOptions = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Dr.', label: 'Dr.' }
];

export interface FileUploads {
  passportUpload?: File[];
  postSecondaryUpload?: File[];
  resumeUpload?: File[];
  canadianUpload?: File[];
  marriageUpload?: File[];
  familyPassportUpload?: File[];
  familyBirthCertUpload?: File[];
}

export type base64Uploads = {
  passportUpload?: string[];
  postSecondaryUpload?: string[];
  resumeUpload?: string[];
  canadianUpload?: string[];
  marriageUpload?: string[];
  familyPassportUpload?: string[];
  familyBirthCertUpload?: string[];
};

export const NewApplication = ({ onSubmitForm }: ApplicationFormProps) => {
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, setValue, getValues, trigger } = useForm<ApplicationFormFields>(); 
  const [uploads, setUploads] = useState<FileUploads>({});
  const [documentNames, setDocumentNames] = useState<DocumentNames>({ localDocuments: [], uploadedDocuments: [] } as DocumentNames);
  const [provinceOptions, setProvinceOptions] = useState<DropdownOptions[]>([]);
  const lang = localStorage.getItem('lang') || 'en';

  const {
    data: countryOptions,
    error: errorCountryOptions,
    isLoading: countryOptionsIsLoading,
  } = useGetCountries();

  const validateBirthday = () => {
    return new Promise((resolve, reject) => {
      const value = getValues("birthday");
      
      // Not a required field.
      if (!value) {
        clearErrors("birthday");
        resolve(true);
        return;
      }
  
      // Try to parse the date
      const parsedDate = parse(value, 'yyyy-M-d', new Date());
  
      // Check if it's a valid date
      if (!isValid(parsedDate)) {
        setError("birthday", { 
          type: "invalid", 
          message: "Please enter a valid date" 
        });
        reject(new Error("Please enter a valid date"));
        return;
      }
  
      // Check if date is in the future
      if (isFuture(parsedDate)) {
        setError("birthday", { 
          type: "future", 
          message: "Date of Birth cannot be in the future" 
        });
        reject(new Error("Date of Birth cannot be in the future"));
        return;
      }
  
      // Check if date is too far in the past (e.g., over 120 years)
      const minDate = subYears(new Date(), 120);
      if (parsedDate < minDate) {
        setError("birthday", { 
          type: "past", 
          message: "Please enter a Date of Birth in the last 120 years" 
        });
        reject(new Error("Please enter a Date of Birth in the last 120 years"));
        return;
      }
  
      // If we get here, the date is valid
      clearErrors("birthday");
      resolve(true);
    });
  };

  const customValidation = async () => { 
    try {
      await validateBirthday();
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      return false;
    }
  };
  const onSubmit = async (values: ApplicationFormFields) => {
    // Perform additional validation to catch autocomplete bypass issues
    const requiredFields = [
      'textRequestDetails',
      'firstName',
      'lastName',
      'streetName',
      'town',
      'province',
      'country',
      'email',
    ];

    const missingFields = requiredFields.filter(field => {
      return !values[field as keyof ApplicationFormFields];
    });

    if (missingFields.length > 0) {
      missingFields.forEach(field => {
        setError(field as keyof ApplicationFormFields, {
          type: 'required',
          message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
        });
      });
      
      // Scroll to the first error
      document.getElementsByClassName(style.formContent)[0]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      
      return;
    }

    try {
      await validateBirthday();
      await handleEmailBlur();
      
      const uploadsBase64Promises = Object.entries(uploads).flatMap(([uploadType, files]) => {
        return files?.map((file: File) => 
          fileToBase64(file).then(base64 => {
            const extension = file.name.split('.').pop();  
            const base64RemoveExtension = base64.split(',')[1];
            return { 
              uploadType, 
              base64: base64RemoveExtension, 
              docExt: extension, 
              docName: file.name 
            };
          })
        ) || [];
      });
    
      const base64Results = await Promise.all(uploadsBase64Promises);
    
      // Group the Base64 objects by their uploadType
      const groupedBase64 = base64Results.reduce((acc, { uploadType, base64, docExt, docName }) => {
        if (!acc[uploadType]) {
          acc[uploadType] = [];
        }
        acc[uploadType].push({ base64, docExt, docName });
        return acc;
      }, {} as Record<string, Array<{ base64: string, docExt: string, docName: string }>>);
    
      onSubmitForm({ ...values, fileUploads: groupedBase64 });
    } catch (e) {
      console.error(e);
    }
  };
  
  const handleFormValueChange = (name: string, value: string) => {
    if (name === "birthday") {
      clearErrors("birthday");
    }
    setValue(name as keyof ApplicationFormFields, value);
    trigger(name as keyof ApplicationFormFields);
  };

  const handleCountryDropDownChange = (value: string) => {
    handleFormValueChange("country", value);

    const selectedCountry = countryOptions?.find(
      (country) => country.value === value
    );
    const hasProvinces = selectedCountry?.hasProvinceOrState || false;

    if (hasProvinces) {
      setProvinceOptions(selectedCountry?.provinceOrStates || []);

    } else {
      setProvinceOptions([]);
      setValue("province", "");
    }
  };

  const handleCalendarChange = (name: string, value: string) => {
    if (value) {
      handleFormValueChange(name as keyof ApplicationFormFields, value);
    }
  };

  const addDocuments = ({ files, fieldId }: DocumentAdditionParams) => {
    clearErrors(fieldId as keyof ApplicationFormFields);

    const { supportedFiles, error } = parseFileExtensions(files, AllowedExtensions);
    if (error) {
      setError(fieldId as keyof ApplicationFormFields, error);
    }
    if (supportedFiles.length === 0) return;
    const supportedDocNameSet = new Set(supportedFiles.map(file => file.name));

    setUploads(prevUploads => {
      const key = fieldId as keyof FileUploads;
      const filteredUploads = Object.entries(prevUploads).reduce<FileUploads>((acc, [key, existingFiles]: [string, File[]]) => {
        return {
          ...acc,
          [key]: existingFiles.filter(file => !supportedDocNameSet.has(file.name)),
        };
      }, {} as FileUploads);
      
      const currentFiles = filteredUploads[key] || [];

      const updatedUploads = { 
        ...filteredUploads, 
        [key]: [...currentFiles, ...supportedFiles]
      };
      return updatedUploads;
    });

    setDocumentNames(prevDocumentNames => {
      const newDocuments = supportedFiles.map(file => {
        return {
          name: file.name,
          fieldId: fieldId,
          permanent: false,
        } as DocumentName;
      });
      const filteredDocumentNames = {
        ...prevDocumentNames,
        localDocuments: prevDocumentNames.localDocuments.filter(doc => !supportedDocNameSet.has(doc.name)),
      };
      const updatedDocumentNames = {
        ...filteredDocumentNames,
        localDocuments: [...filteredDocumentNames.localDocuments, ...newDocuments],
      };
      
      return updatedDocumentNames;
    });
  };

  const deleteDocument = ({ name, fieldId }: DocumentDeletionParams) => {
    clearErrors(fieldId as keyof ApplicationFormFields);

    setUploads(prevUploads => {
      const key = fieldId as keyof FileUploads;
      return {
        ...prevUploads,
        [key]: prevUploads[key]?.filter(file => file.name !== name),
      };
    });
    setDocumentNames(prevDocumentNames => {
      return {
        ...prevDocumentNames,
        localDocuments: prevDocumentNames.localDocuments.filter(doc => doc.name !== name || doc.fieldId !== fieldId),
      } as DocumentNames;
    });
  };

  const requestDetailsValidationSchema = {
    required: lang === 'fr' ? translations.fr.details_required : translations.en.details_required,
    maxLength: {
      value: 1000,
      message: lang === 'fr' ? translations.fr.character_limit : translations.en.character_limit
    }
  };

  const phoneValidationSchema = { 
    minLength: {
      value: 10,
      message: lang === 'fr' ? translations.fr.telephone_length : translations.en.telephone_length,
    },
    pattern: {
      value: validPhoneNumberRegex,
      message: lang === 'fr' ? translations.fr.check_telephone_format : translations.en.check_telephone_format,
    },
  };

  const emailValidationSchema = {
    required: lang === 'fr' ? translations.fr.email_required : translations.en.email_required,
    pattern: {
      value: validEmailRegex,
      message: lang === 'fr' ? translations.fr.email_format_invalid : translations.en.email_format_invalid,
    }
  };

  const postalZipCodeValidationSchema = { 
    pattern: {
      value: validZipCodePostalCodeRegex,
      message: lang === 'fr' ? translations.fr.invalid_format : translations.en.invalid_format,
    },
  };

  const commonProvinceProps = {
    id: "province",
    validationSchema: { required: lang === 'fr' ? translations.fr.state_required : translations.en.state_required },
    labelClass: style.labels,
    placeholder: "State/Province",
    register: register,
    errors: errors,
  };
 
  const handleEmailBlur = async () => {
    const email = getValues("email");
    if (email && validEmailRegex.test(email)) {
      try {
        await getUserEmailCheck({ email });
        clearErrors("email");
      } catch (error) {
        setError("email", {
          type: "manual",
          message: lang === 'fr' ? translations.fr.email_registered : translations.en.email_registered
        });
        throw new Error("This email is already registered. Please use a different email.");
      }
    }
  };

  return (
    <div className={style.formContent}>
      <form onSubmit={handleSubmit(onSubmit)} className={style.applicationForm}>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>1</span> {lang === 'fr' ? translations.fr.how_can_we_help : translations.en.how_can_we_help}</h4>
          </div>

          <div className={style.row}>
            <MultilineInput
              id="textRequestDetails"
              label={""}
              placeholder={lang === 'fr' ? translations.fr.request_details_placeholder : translations.en.request_details_placeholder}
              value={getValues("textRequestDetails")}
              onChange={(value) => handleFormValueChange("textRequestDetails", value)}
              rows={6}
              isReview={false}
              isComments={false}
              register={register}
              errors={errors}
              validationSchema={requestDetailsValidationSchema}
            />
          </div>
        </div>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>2</span> {lang === 'fr' ? translations.fr.applicant_details : translations.en.applicant_details}</h4>
          </div>

          <div className={style.row}>
            <div className={style.rowColumnOne}>
              <DropDownSelect 
                id='title'
                options={titleOptions}
                onChange={(value) => handleFormValueChange('title', value)} 
                placeholder={lang === 'fr' ? translations.fr.title : translations.en.title}
                title={lang === 'fr' ? translations.fr.title : translations.en.title}
                register={register}
                errors={errors}
                isRequiredMessage={lang === 'fr' ? translations.fr.select_title : translations.en.select_title}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id="firstName"
                label={lang === 'fr' ? translations.fr.given_names_passport : translations.en.given_names_passport}
                register={register} 
                value={getValues("firstName")}
                onChange={(value) => handleFormValueChange("firstName", value)}
                placeholder={lang === 'fr' ? translations.fr.first_name : translations.en.first_name}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: lang === 'fr' ? translations.fr.given_name_required : translations.en.given_name_required }}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id="lastName"
                label={lang === 'fr' ? translations.fr.last_name_passport : translations.en.last_name_passport}
                register={register} 
                value={getValues("lastName")}
                onChange={(value) => handleFormValueChange("lastName", value)}
                placeholder={lang === 'fr' ? translations.fr.last_name : translations.en.last_name}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: lang === 'fr' ? translations.fr.last_name_required : translations.en.last_name_required }}
              />
            </div>
          </div>
        
          <div className={style.row}>
            <div className={style.rowColumnOne}>
              <DatePicker
                id="birthday"
                label={lang === 'fr' ? translations.fr.date_of_birth : translations.en.date_of_birth}
                className={style.inputField} 
                register={register}
                value={parseDateString(getValues("birthday"))}
                onChange={(value) => handleCalendarChange("birthday", value)}
                errors={errors}
                maxDate={new Date()}
                minDate={new Date(new Date().setFullYear(new Date().getFullYear() -100))}
                placeholder={lang === 'fr' ? translations.fr.yyyy_mm_dd : translations.en.yyyy_mm_dd}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <DropDownSelect
                id={'nationality'}
                options={countryOptions || []}
                onChange={(value) => handleFormValueChange('nationality', value)} 
                register={register}
                errors={errors}
                placeholder={lang === 'fr' ? translations.fr.nationality : translations.en.nationality}
                title={lang === 'fr' ? translations.fr.nationality : translations.en.nationality}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id="passportNumber"
                label={lang === 'fr' ? translations.fr.passport_number : translations.en.passport_number}
                register={register} 
                value={getValues("passportNumber")}
                onChange={(value) => handleFormValueChange("passportNumber", value)}
                placeholder={lang === 'fr' ? translations.fr.passport_number : translations.en.passport_number}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
              />
            </div>
          </div>
        </div>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>3</span> {lang === 'fr' ? translations.fr.applicant_contact : translations.en.applicant_contact}</h4>
          </div>
          <div className={style.row}>
            <div className={style.rowColumnTwo}>
              <Input
                id='streetName'
                label={lang === 'fr' ? translations.fr.street_address : translations.en.street_address}
                register={register} 
                value={getValues("streetName")}
                onChange={(value) => handleFormValueChange("streetName", value)}
                placeholder={lang === 'fr' ? translations.fr.street_address : translations.en.street_address}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: lang === 'fr' ? translations.fr.street_address_required : translations.en.street_address_required }}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input 
                id='unitNumber'
                label={lang === 'fr' ? translations.fr.unit_number2 : translations.en.unit_number}
                register={register} 
                value={getValues("unitNumber")}
                onChange={(value) => handleFormValueChange("unitNumber", value)}
                placeholder={lang === 'fr' ? translations.fr.unit_number2 : translations.en.unit_number}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.rowColumnTwo}>
              <Input
                id='town'
                label={lang === 'fr' ? translations.fr.town_city : translations.en.town_city}
                register={register} 
                value={getValues("town")}
                onChange={(value) => handleFormValueChange("town", value)}
                placeholder={lang === 'fr' ? translations.fr.town_city : translations.en.town_city}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: lang === 'fr' ? translations.fr.town_city_required : translations.en.town_city_required }}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <DropDownSelect 
                id={'country'}
                options={countryOptions ? countryOptions : []}
                onChange={handleCountryDropDownChange} 
                register={register}
                errors={errors}
                placeholder={lang === 'fr' ? translations.fr.country : translations.en.country}
                title={lang === 'fr' ? translations.fr.country : translations.en.country}   
                isRequiredMessage={lang === 'fr' ? translations.fr.country_required : translations.en.country_required}
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.rowColumnTwo}>
              <Input
                id='postal'
                label={lang === 'fr' ? translations.fr.postal_code_zip_code : translations.en.postal_code_zip_code}
                register={register} 
                value={getValues("postal")}
                onChange={(value) => handleFormValueChange("postal", value)}
                placeholder={lang === 'fr' ? translations.fr.postal_code_zip_code : translations.en.postal_code_zip_code}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={postalZipCodeValidationSchema}
              />
            </div>
            <div className={style.rowColumnTwo}>
              {provinceOptions && provinceOptions.length > 0 ? 
                <DropDownSelect
                  title={lang === 'fr' ? translations.fr.province_state : translations.en.province_state}
                  options={provinceOptions}
                  onChange={value => handleFormValueChange("province", value)}
                  startingValue={getValues("province") || undefined}
                  {...commonProvinceProps}
                /> : 
                <Input 
                  label={lang === 'fr' ? translations.fr.province_state : translations.en.province_state}
                  value={getValues("province")}
                  onChange={value => handleFormValueChange("province", value)}
                  inputClass={style.inputField} 
                  locked={isEmptyDropDownValue(getValues("country"))}
                  {...commonProvinceProps}
                />
              }
            </div>
          </div>

          <div className={style.row}>
            <div className={style.rowColumnTwo}>
              <Input
                id='email'
                label={lang === 'fr' ? translations.fr.email : translations.en.email}
                register={register} 
                value={getValues("email")}
                onChange={(value) => handleFormValueChange("email", value)}
                onBlur={handleEmailBlur}
                placeholder={lang === 'fr' ? translations.fr.email : translations.en.email}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={emailValidationSchema}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id='phone'
                label={lang === 'fr' ? translations.fr.telephone : translations.en.telephone}
                register={register} 
                value={getValues("phone")}
                onChange={(value) => handleFormValueChange("phone", value)}
                placeholder={lang === 'fr' ? translations.fr.telephone : translations.en.telephone}
                inputClass={style.inputField} 
                labelClass={style.labels} 
                errors={errors} 
                type='tel'
                validationSchema={phoneValidationSchema}
              />
            </div>
          </div>
        </div>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>4</span> {lang === 'fr' ? translations.fr.applicant_documents : translations.en.applicant_documents}</h4>
          </div>
          <div className={style.gridContainer}>
            <FileUpload
              id="passportUpload"
              label={lang === 'fr' ? translations.fr.passport_bio_page : translations.en.passport_bio_page}
              className={style.fileUpload}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "passportUpload")}
              errors={errors}
            />

            <FileUpload
              id="postSecondaryUpload"
              label={lang === 'fr' ? translations.fr.post_secondary_education_docs : translations.en.post_secondary_education_docs}
              className={style.fileUpload}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "postSecondaryUpload")}
              errors={errors}
            />

            <FileUpload
              id="resumeUpload"
              label={lang === 'fr' ? translations.fr.resume : translations.en.resume}
              subLabel={lang === 'fr' ? translations.fr.resume_description : translations.en.resume_description}
              className={style.fileUpload}
              subLabelClass={style.fileUploadSubtext}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "resumeUpload")}
              errors={errors}
            />

            <FileUpload
              id="canadianUpload"
              label={lang === 'fr' ? translations.fr.canadian_status_document : translations.en.canadian_status_document}
              subLabel={lang === 'fr' ? translations.fr.canadian_status_description : translations.en.canadian_status_description}
              className={style.fileUpload}
              subLabelClass={style.fileUploadSubtext}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "canadianUpload")}
              errors={errors}
            />

            <FileUpload
              id="marriageUpload"
              label={lang === 'fr' ? translations.fr.marriage_certificate : translations.en.marriage_certificate}
              className={style.fileUpload}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "marriageUpload")}
              errors={errors}
            />
          </div>

          <div className={style.rowColumnTwo}>
            <p className={style.familyMembers}>{lang === 'fr' ? translations.fr.accompanying_family_documents : translations.en.accompanying_family_documents}</p>
          </div>

          <div className={style.gridContainer}>
            <FileUpload
              id="familyPassportUpload"
              label={lang === 'fr' ? translations.fr.passport_bios_pages_family : translations.en.passport_bios_pages_family}
              className={style.fileUpload}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "familyPassportUpload")}
              errors={errors}
            />
            <FileUpload
              id="familyBirthCertUpload"
              label={lang === 'fr' ? translations.fr.birth_certificates_dependants : translations.en.birth_certificates_dependants}
              className={style.fileUpload} 
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "familyBirthCertUpload")}
              errors={errors}
            />
          </div>
        </div>


        <div className={style.rowColumnTwo}>
          <div className={style.submitSection}>
            <Button 
              onClick={async () => {
                const isValid = await customValidation();
                if (isValid) {
                  handleSubmit(onSubmit)();
                }
              }} 
              id={'applicationSubmit'} 
              className={style.submitButton} 
              type="button" 
              style='gradient'
            >
              {lang === 'fr' ? translations.fr.submit : translations.en.submit}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};